import { ApiError, api } from "./api";

/*
 * Web-side contract for the session API. The Worker implements the same paths; shapes follow
 * docs/api/internal-endpoints.md (UserDto, workspace list) so the SPA and the extension share serializers.
 */

// `/api/auth/me` top-level settings send LIGHT | DARK; user.settings keeps Clockify's DEFAULT for the extension.
export type ThemeSetting = "DEFAULT" | "LIGHT" | "DARK";
export type TimeFormat = "HOUR12" | "HOUR24";
export type WeekStart = "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY";

export interface UserSettings {
  theme?: ThemeSetting;
  lang?: string;
  timeZone?: string;
  timeFormat?: TimeFormat;
  dateFormat?: string;
  weekStart?: WeekStart;
  myStartOfDay?: string;
  projectPickerTaskFilter?: boolean;
  sendNewsletter?: boolean;
  [key: string]: unknown;
}

export type UserStatus = "ACTIVE" | "PENDING_EMAIL_VERIFICATION" | "DELETED" | "NOT_REGISTERED" | "LIMITED";

export interface User {
  id: string;
  email: string;
  name: string;
  profilePicture?: string | null;
  status?: UserStatus;
  activeWorkspace?: string | null;
  defaultWorkspace?: string | null;
  settings?: UserSettings;
}

/** Clone extension on the workspace list: the viewer's role there, so the shell can gate owner/admin entries. */
export type WorkspaceRole = "OWNER" | "ADMIN" | "PROJECT_MANAGER" | "TEAM_MANAGER" | "MEMBER";

/** CurrencyWithDefaultInfoDtoV1, as the workspace DTO lists them (Workspace settings > Finances). */
export interface WorkspaceCurrency {
  id: string;
  code: string;
  isDefault: boolean;
}

export interface Workspace {
  id: string;
  name: string;
  imageUrl?: string | null;
  role?: WorkspaceRole;
  workspaceSettings?: Record<string, unknown>;
  features?: string[];
  currencies?: WorkspaceCurrency[];
}

export interface MeResponse {
  user: User;
  workspaces: Workspace[];
  activeWorkspaceId: string | null;
  settings: UserSettings;
  /** False only while a mailer is configured and the address is unconfirmed. */
  emailVerified?: boolean;
}

export interface InviteInfo {
  workspaceName: string;
  inviterName: string;
  email: string;
  userExists: boolean;
}

/** Resolves to null when there is no session (401), so callers branch without try/catch. */
export async function fetchMe(): Promise<MeResponse | null> {
  try {
    return await api<MeResponse>("/api/auth/me");
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

export function login(email: string, password: string) {
  return api<{ user: User }>("/api/auth/login", { method: "POST", body: { email, password } });
}

export function signup(name: string, email: string, password: string, lang?: string) {
  return api<{ user: User }>("/api/auth/signup", { method: "POST", body: { name, email, password, lang } });
}

export function logout() {
  return api<void>("/api/auth/logout", { method: "POST" });
}

/** Full-page navigation target: OAuth must leave the SPA, so this is used as an <a href>, never fetched. */
export function googleStartUrl(redirect: string): string {
  return `/api/auth/google/start?redirect=${encodeURIComponent(redirect)}`;
}

export function forgotPassword(email: string) {
  return api<void>("/api/auth/forgot-password", { method: "POST", body: { email } });
}

export function resetPassword(token: string, password: string) {
  return api<void>("/api/auth/reset-password", { method: "POST", body: { token, password } });
}

export function getInvite(token: string) {
  return api<InviteInfo>(`/api/auth/invites/${encodeURIComponent(token)}`);
}

export function acceptInvite(token: string, body: { name?: string; password?: string }) {
  return api<{ user: User }>(`/api/auth/invites/${encodeURIComponent(token)}/accept`, { method: "POST", body });
}

export function changePassword(currentPassword: string, newPassword: string) {
  return api<void>("/api/auth/change-password", { method: "POST", body: { currentPassword, newPassword } });
}

export function switchWorkspace(userId: string, workspaceId: string) {
  return api<User | undefined>(
    `/api/users/${encodeURIComponent(userId)}/defaultWorkspace/${encodeURIComponent(workspaceId)}`,
    { method: "PUT" },
  );
}

const RETURN_BASE = "https://return.invalid";

/**
 * Accepts only same-origin absolute paths so ?returnUrl= cannot bounce users to another site. The URL parser strips
 * tabs and newlines and treats `\` as `/`, so `/\t/evil.example` becomes `//evil.example`: prefix checks on the raw
 * string are not enough. Parse against a fixed base, require that origin, and return the normalised path.
 */
export function safeReturnUrl(value: string | null | undefined, fallback = "/"): string {
  if (!value?.startsWith("/") || value.includes("\\")) return fallback;
  let u: URL;
  try {
    u = new URL(value, RETURN_BASE);
  } catch {
    return fallback;
  }
  if (u.origin !== RETURN_BASE || u.pathname.startsWith("//")) return fallback;
  return `${u.pathname}${u.search}${u.hash}`;
}
